/**
 * Variant Picker
 * On variant change, updates price, form variant id, add-to-cart button price,
 * optional SKU/inventory, and product gallery slide via product JSON (no Section Rendering API).
 */

function formatMoney(cents, format) {
  if (cents == null) return '';
  const value = (cents / 100).toFixed(2);
  if (!format) return value;
  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (key === 'amount' ? value : ''));
}

class VariantPicker extends HTMLElement {
  constructor() {
    super();
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get productJson() {
    if (this._productJson) return this._productJson;
    const section = this.closest('[data-section-id]');
    if (!section) return null;
    const script = document.getElementById(`product-json-${section.dataset.sectionId}`);
    if (!script) return null;
    try {
      this._productJson = JSON.parse(script.textContent);
      return this._productJson;
    } catch (e) {
      console.warn('VariantPicker: failed to parse product JSON', e);
      return null;
    }
  }

  connectedCallback() {
    this.variantSelectors = this.querySelectorAll('input[type="radio"]');
    this.handleChange = this.handleChange.bind(this);
    this.debounceMs = 300;
    this._debounceTimer = null;

    this.variantSelectors.forEach((sel) => {
      sel.addEventListener('change', this.handleChange);

      // Deselect on re-click: intercept on the label (input is hidden, clicks go through label)
      if (sel.id) {
        const label = this.querySelector(`label[for="${CSS.escape(sel.id)}"]`);
        if (label) {
          label.addEventListener('click', (e) => {
            if (sel.checked) {
              e.preventDefault(); // stop browser from re-firing click on the input
              sel.checked = false;
              if (this._debounceTimer) {
                clearTimeout(this._debounceTimer);
                this._debounceTimer = null;
              }
              this.clearVariant();
            }
          });
        }
      }
    });

    // Restore variant from URL on load
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    if (urlVariantId) {
      const radio = Array.from(this.variantSelectors).find((r) => r.value === urlVariantId);
      if (radio) {
        radio.checked = true;
        this.updateVariant(radio);
      }
    }

    // If no swatch is pre-checked, ensure variant state is cleared
    const anyChecked = Array.from(this.variantSelectors).some((r) => r.checked);
    if (!anyChecked) this.clearVariant();
  }

  disconnectedCallback() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this.variantSelectors?.forEach((sel) => sel.removeEventListener('change', this.handleChange));
  }

  clearVariant() {
    const section = this.closest(`section[data-section-id="${this.sectionId}"]`);

    const variantIdInput = section?.querySelector('input[data-variant-id]');
    if (variantIdInput) variantIdInput.value = '';

    const gallery = section?.querySelector('product-gallery');
    if (gallery && typeof gallery.resetSlides === 'function') {
      gallery.resetSlides();
    } else if (gallery && typeof gallery.setSlide === 'function') {
      gallery.setSlide(0);
    }

    // Remove variant from URL
    const url = new URL(window.location.href);
    url.searchParams.delete('variant');
    history.replaceState({}, '', url.toString());

    this.dispatchEvent(new CustomEvent('variant:cleared', { bubbles: false }));
  }

  handleChange(event) {
    const input = event.currentTarget;
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this._debounceTimer = null;
      this.updateVariant(input);
    }, this.debounceMs);
  }

  updateVariant(input) {
    const variantId = parseInt(input.value, 10);
    const data = this.productJson;
    if (!data || !data.variants) return;

    const variant = data.variants.find((v) => v.id === variantId);
    if (!variant) return;

    const section = this.closest(`section[data-section-id="${this.sectionId}"]`);
    const moneyFormat = data.money_format || '{{amount}}';

    // Form variant id
    const variantIdInput = section?.querySelector('input[data-variant-id]');
    if (variantIdInput) variantIdInput.value = variant.id;

    // Update URL with selected variant
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());

    // Pricing block
    const priceEl = section?.querySelector('[data-variant-price]');
    const compareEl = section?.querySelector('[data-variant-compare-price]');
    const saveEl = section?.querySelector('[data-variant-save]');
    const saveTextEl = section?.querySelector('[data-variant-save-text]');

    if (priceEl) priceEl.textContent = formatMoney(variant.price, moneyFormat);

    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price, moneyFormat);
        compareEl.classList.remove('tw:!hidden');
      } else {
        compareEl.classList.add('tw:!hidden');
      }
    }

    if (saveEl) {
      if (variant.compare_at_price > variant.price) {
        const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        if (saveTextEl) saveTextEl.textContent = `Save ${pct}%`;
        saveEl.classList.remove('tw:!hidden');
      } else {
        saveEl.classList.add('tw:!hidden');
      }
    }

    // Add to cart button price
    const addToCartPrice = section?.querySelector('[data-add-to-cart-price]');
    if (addToCartPrice) addToCartPrice.textContent = formatMoney(variant.price, moneyFormat);

    // SKU / inventory
    const skuEl = section?.querySelector('[data-variant-sku-value]');
    const invEl = section?.querySelector('[data-variant-inventory-quantity]');
    if (skuEl) skuEl.textContent = variant.sku != null && variant.sku !== '' ? variant.sku : 'N/A';
    if (invEl) invEl.textContent = variant.inventory_quantity ?? 0;

    // Gallery: filter to variant's featured image + model images
    const gallery = section?.querySelector('product-gallery');
    if (gallery && variant.featured_media_index != null) {
      if (typeof gallery.filterSlides === 'function') {
        gallery.filterSlides(variant.featured_media_index);
      } else if (typeof gallery.setSlide === 'function') {
        gallery.setSlide(variant.featured_media_index);
      }
    }
  }
}

customElements.define('variant-picker', VariantPicker);
